import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
// Issue #860: Use RBAC utility functions
import { isAdmin } from '../utils/rbac';
import { extractApiError } from '../utils/errorUtils';
import { checkAuthenticationWithRetry, buildAdminLoginRedirectUrl } from '../utils/authHelper';

interface AdminAuthGuardProps {
  children: React.ReactNode;
}

const AdminAuthGuard: React.FC<AdminAuthGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if user has token and is logged in
        const loggedIn = authApi.isLoggedIn();

        if (loggedIn) {
          // Verify token with timeout and retry logic
          const result = await checkAuthenticationWithRetry();

          if (result.success) {
            setIsAuthenticated(true);
            // Issue #860: Use RBAC utility function to check admin role
            setIsAuthorized(isAdmin());
          } else {
            // Authentication failed after retries - logout and redirect
            authApi.logout();
            setIsAuthenticated(false);
            setIsAuthorized(false);
          }
        } else {
          setIsAuthenticated(false);
          setIsAuthorized(false);
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Authentication check failed:', apiError);
        setIsAuthenticated(false);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying admin permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated or not authorized (admin only - Issue #147)
  if (!isAuthenticated || !isAuthorized) {
    // Preserve the attempted URL to redirect back after login
    const redirectPath = location.pathname + location.search;
    const loginPath = buildAdminLoginRedirectUrl(redirectPath);

    return <Navigate to={loginPath} replace />;
  }

  // User is authenticated and authorized as admin, render children
  return <>{children}</>;
};

export default AdminAuthGuard;